/* TENSO — bag count, pulse, floating cue, and cart drawer refresh.

   Two things were stale before this file existed: the header count, which was
   server-rendered only, and the drawer, which was hardcoded markup in
   theme.liquid that always said "Your bag is empty" whatever was in the cart.

   Both are correct on page load from Liquid alone. This file only refreshes an
   already-correct view. If it never loads you lose live updates until the next
   navigation — exactly where things stood before. Nothing here decides whether
   content exists.

   Truth comes from the server, never arithmetic on the client: /cart.js for the
   count, the Section Rendering API for the drawer. Rebuilding drawer markup in
   JS would duplicate cart-drawer.liquid and drift from it.

   THE THREE-EVENT HANDSHAKE
   Measured: /cart/add.js takes 560-690ms. Waiting for it before showing any
   feedback read as lag, so the chip is raised on 'pending' — the instant the tap
   happens — but WITHOUT a count, because the server has not agreed a quantity
   yet. 'added' fills the number in and pulses it. 'failed' retracts the chip, so
   a failed add never leaves a confirmation on screen.

     tenso:bag:pending  → chip appears, no number
     tenso:bag:added    → number arrives + pulse, drawer refreshes, drawer OPENS
     tenso:bag:changed  → silent refresh, drawer left as it is
     tenso:bag:failed   → chip retracts

   The chip RESTS in the pending state, collapsed. Rendering it expanded meant
   every add faded it in at full width, collapsed it, then expanded it again —
   visibly glitchy. Resting collapsed means the badge expands exactly once, when
   the number arrives.

   NO requestAnimationFrame anywhere. An earlier version faded the cue in inside
   a double rAF to force a style recalculation. That works in a foreground tab
   and silently does nothing in a background one — rAF is not guaranteed to fire
   — leaving the cue stuck invisible. Measured it happening. Everything here is a
   class toggle on an already-laid-out node, plus one synchronous reflow to
   restart an animation.

   OPEN ON ADD
   'added' opens the drawer AFTER the refresh resolves, never before. Opening
   first shows the previous cart for one frame and then swaps under the reader,
   which reads as a glitch. tenso.js owns the drawer and exposes
   window.tensoCartOpen; we call that rather than toggling its classes, so the
   scroll lock and the nav-drawer mutual exclusion stay in one place.

   DELEGATION
   Both click handlers are bound to document, not to the controls. swapDrawer
   replaces innerHTML on every refresh, so anything bound directly to a remove
   link or an add button would be destroyed by the first swap.
*/
(function(){
  "use strict";

  var SECTION_ID  = 'cart-drawer';
  var FLOAT_MS    = 2400;   /* how long the chip stays once resolved */
  var PENDING_MAX = 8000;   /* a hung request must not strand the chip on screen */
  var floatTimer  = null;
  var pendingTimer= null;

  function rootPath(){
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function all(sel){ return [].slice.call(document.querySelectorAll(sel)); }
  function floatEl(){ return document.querySelector('[data-bag-float]'); }
  function fire(name){ document.dispatchEvent(new CustomEvent(name)); }

  /* ---------- count ---------- */

  function paint(count){
    all('[data-cart-count]').forEach(function(b){
      b.textContent = count;
      b.classList.toggle('is-empty', !count);
    });
  }

  /* Restarting a CSS animation needs the class removed, a reflow, then the class
     re-added. Reading offsetWidth forces that reflow synchronously — no rAF, so
     it behaves identically in a background tab. */
  function bump(){
    all('[data-cart-count]').forEach(function(el){
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  }

  /* ---------- floating cue ----------
     Only when the header bag is actually off screen. If it is still in view the
     pulse alone is the feedback, and a floating copy of a visible thing is just
     noise. */

  function headerBagOffScreen(){
    var el = document.querySelector('[data-cart-open]');
    if(!el) return false;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom < 8 || r.top > vh - 8;
  }

  /* Hiding also resets the badge to collapsed, ready for the next add. Safe to do
     in the same tick as removing is-shown: while the chip is not shown the CSS
     kills the badge transition, so the reset cannot be seen. */
  function floatHide(){
    var f = floatEl(); if(!f) return;
    clearTimeout(floatTimer); clearTimeout(pendingTimer);
    f.classList.remove('is-shown');
    f.classList.add('is-pending');
  }

  /* tap: chip in, no number yet. is-pending is already on at rest, so this adds
     no width change to the reveal. */
  function floatPending(){
    var f = floatEl(); if(!f) return;
    if(!headerBagOffScreen()) return;
    clearTimeout(floatTimer); clearTimeout(pendingTimer);
    f.classList.add('is-pending');
    f.classList.add('is-shown');
    pendingTimer = setTimeout(floatHide, PENDING_MAX);
  }

  /* server confirmed: let the number expand in, then fade on the usual delay */
  function floatResolve(){
    var f = floatEl(); if(!f) return;
    clearTimeout(pendingTimer);
    if(!f.classList.contains('is-shown')) return;   /* header bag was visible */
    f.classList.remove('is-pending');
    clearTimeout(floatTimer);
    floatTimer = setTimeout(floatHide, FLOAT_MS);
  }

  /* ---------- drawer ----------
     Only the two marked regions are swapped. The drawer header and its close
     button are deliberately left alone: tenso.js binds a direct listener to
     [data-cart-close] once at DOMContentLoaded, so replacing that node would
     silently break closing the drawer. */

  function swapDrawer(html){
    var doc = new DOMParser().parseFromString(html, 'text/html');
    ['[data-cart-items]', '[data-cart-foot]'].forEach(function(sel){
      var fresh = doc.querySelector(sel);
      var live  = document.querySelector(sel);
      if(fresh && live) live.innerHTML = fresh.innerHTML;
    });
  }

  function refreshDrawer(){
    return fetch(rootPath() + '?sections=' + SECTION_ID, { headers: { 'Accept': 'application/json' } })
      .then(function(r){ return r.json(); })
      .then(function(json){ if(json && json[SECTION_ID]) swapDrawer(json[SECTION_ID]); })
      .catch(function(){ /* drawer keeps the last good render */ });
  }

  /* tenso.js owns open/close. If it has not bound yet, do nothing rather than
     reaching into the drawer's classes and leaving the scroll lock off. */
  function openDrawer(){
    if(window.tensoCartOpen) window.tensoCartOpen();
  }

  /* ---------- refresh ---------- */

  function refresh(announce){
    var count = fetch(rootPath() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r){ return r.json(); })
      .then(function(cart){
        paint(cart.item_count);
        if(announce){ floatResolve(); bump(); }
      })
      .catch(function(){ floatHide(); });
    return Promise.all([count, refreshDrawer()]);
  }

  /* ---------- remove a line ----------
     The link keeps a working href for the no-JS case. Intercepted here so the
     reader stays on the page: following it navigates to /cart.

     Line numbers are 1-based and shift as lines are removed, but the markup is
     re-rendered from the server after every change, so the numbers in the DOM
     are always the ones the server currently agrees with. */
  function bindRemove(){
    document.addEventListener('click', function(e){
      if(!e.target || !e.target.closest) return;
      var link = e.target.closest('.cd__remove');
      if(!link) return;
      var line = parseInt(link.getAttribute('data-line'), 10);
      if(!line) return;                       /* no data-line — let the href work */
      e.preventDefault();
      if(link.dataset.busy) return;
      link.dataset.busy = '1';
      fetch(rootPath() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: 0 })
      })
      .then(function(r){
        if(!r.ok) throw new Error('change failed');
        fire('tenso:bag:changed');
      })
      .catch(function(){
        /* Fall back to the plain navigation rather than silently doing nothing. */
        window.location.href = link.getAttribute('href');
      });
    });
  }

  /* ---------- complete the set ----------
     The drawer's add control. Fires 'changed' rather than 'added': the drawer is
     already open and open-on-add would be a no-op at best and a flicker at worst.
     The count still repaints because refresh() runs either way. */
  function bindQuickAdd(){
    document.addEventListener('click', function(e){
      if(!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-cart-add]');
      if(!btn) return;
      e.preventDefault();
      var id = parseInt(btn.getAttribute('data-cart-add'), 10);
      if(!id || btn.disabled) return;
      btn.disabled = true;
      fire('tenso:bag:pending');
      fetch(rootPath() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      })
      .then(function(r){
        if(!r.ok) throw new Error('add failed');
        fire('tenso:bag:changed');
      })
      .catch(function(){
        btn.disabled = false;
        fire('tenso:bag:failed');
      });
    });
  }

  document.addEventListener('tenso:bag:pending', floatPending);
  document.addEventListener('tenso:bag:failed',  floatHide);
  document.addEventListener('tenso:bag:added',   function(){ refresh(true).then(openDrawer); });
  document.addEventListener('tenso:bag:changed', function(){ refresh(false); });

  bindRemove();
  bindQuickAdd();

  window.TensoBag = {
    refresh: refresh, bump: bump,
    floatPending: floatPending, floatResolve: floatResolve, floatHide: floatHide,
    refreshDrawer: refreshDrawer, openDrawer: openDrawer
  };
})();
